import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const EngineerChangeResponseModal = ({ changeRequest, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    remarks: '',
    decision: 'approved',
    cost_impact: '',
    timeline_impact: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post(`/change-requests/${changeRequest.request_id}/engineer-respond`, {
        remarks: formData.remarks,
        decision: formData.decision,
        cost_impact: formData.cost_impact ? parseFloat(formData.cost_impact) : null,
        timeline_impact: formData.timeline_impact
      });

      toast.success('Response submitted for admin approval');
      setFormData({ remarks: '', decision: 'approved', cost_impact: '', timeline_impact: '' });
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Respond to Change Request</DialogTitle>
          <DialogDescription>
            Provide your assessment and decision for this change request
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 p-3 bg-stone-50 border border-stone-200 rounded-sm">
          <p className="text-sm text-stone-600 mb-1">Request from: {changeRequest?.requested_by_name}</p>
          <p className="text-sm text-stone-600 mb-1">Stage: {changeRequest?.stage}</p>
          <p className="text-sm text-stone-950 font-medium">{changeRequest?.description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="decision">Decision *</Label>
            <Select
              value={formData.decision}
              onValueChange={(value) => setFormData({ ...formData, decision: value })}
              required
            >
              <SelectTrigger id="decision">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approve</SelectItem>
                <SelectItem value="rejected">Reject</SelectItem>
                <SelectItem value="needs_info">Request More Information</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cost_impact">Cost Impact (₹)</Label>
            <Input
              id="cost_impact"
              type="number"
              value={formData.cost_impact}
              onChange={(e) => setFormData({ ...formData, cost_impact: e.target.value })}
              placeholder="e.g., 45000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="timeline_impact">Timeline Impact</Label>
            <Input
              id="timeline_impact"
              value={formData.timeline_impact}
              onChange={(e) => setFormData({ ...formData, timeline_impact: e.target.value })}
              placeholder="e.g., +5 days"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks *</Label>
            <Textarea
              id="remarks"
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Provide detailed feedback..."
              rows={4}
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Your response will be reviewed by admin before being shared with the customer.
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-700 hover:bg-orange-800"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Response
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EngineerChangeResponseModal;
